<template>
  <section class="change-password-page">
    <div v-if="showErr" class="alert-line alert-line--danger">
      {{ noValidMessage }}
    </div>
    <div v-if="showSuccess" class="alert-line alert-line--success">
      {{ t('ready') }}
    </div>

    <div class="form-wrap">
      <div class="input__group">
        <input
          id="old-password"
          v-model="oldPassword"
          autocomplete="off"
          class="password__input"
          :type="showPasswordOld"
          :placeholder="t('old_password')"
        />
        <button type="button" class="icon__prepend without-outline" @click="togglePassword('old')">
          <span class="material-icons">{{ showPasswordOld === 'text' ? 'visibility' : 'visibility_off' }}</span>
        </button>
      </div>

      <div class="input__group">
        <input
          id="new-password"
          v-model="newPassword"
          autocomplete="off"
          class="password__input"
          :type="showPasswordNew"
          :placeholder="t('new_password')"
        />
        <button type="button" class="icon__prepend without-outline" @click="togglePassword('new')">
          <span class="material-icons">{{ showPasswordNew === 'text' ? 'visibility' : 'visibility_off' }}</span>
        </button>
      </div>

      <div class="input__group">
        <input
          id="repeat-password"
          v-model="repeatPassword"
          autocomplete="off"
          class="password__input"
          :type="showPasswordRepeat"
          :placeholder="t('repeat_password')"
        />
        <button type="button" class="icon__prepend without-outline" @click="togglePassword('repeat')">
          <span class="material-icons">{{ showPasswordRepeat === 'text' ? 'visibility' : 'visibility_off' }}</span>
        </button>
      </div>

      <div class="buttons__group mt-4">
        <button
          type="button"
          class="button__body"
          :disabled="checkToReady() || isSaving"
          @click="sendChangePassword"
        >
          <i class="fa fa-check mr-2" aria-hidden="true"></i>{{ t('change-password') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import http from '@/services/http';
import { useTopbarStore } from '@/stores/topbar';

const router = useRouter();
const { t } = useI18n();
const topbarStore = useTopbarStore();

const showPasswordOld = ref('password');
const showPasswordNew = ref('password');
const showPasswordRepeat = ref('password');

const oldPassword = ref('');
const newPassword = ref('');
const repeatPassword = ref('');

const noValidMessage = ref('');
const showErr = ref(false);
const showSuccess = ref(false);
const isSaving = ref(false);

function togglePassword(kind) {
  if (kind === 'old') showPasswordOld.value = checkType(showPasswordOld.value);
  if (kind === 'new') showPasswordNew.value = checkType(showPasswordNew.value);
  if (kind === 'repeat') showPasswordRepeat.value = checkType(showPasswordRepeat.value);
}

function checkType(type) {
  return type === 'text' ? 'password' : 'text';
}

function validation(body) {
  if (body === 'new') {
    const re = /^[a-zA-Z\d]{6,16}$/;
    return re.test(String(newPassword.value || ''));
  }
  if (body === 'repeat') {
    return String(repeatPassword.value || '') === String(newPassword.value || '');
  }
  return false;
}

function checkToReady() {
  if (newPassword.value !== '' && repeatPassword.value !== '') {
    return oldPassword.value === '';
  }
  return true;
}

async function sendChangePassword() {
  showErr.value = false;
  showSuccess.value = false;
  noValidMessage.value = '';

  if (!validation('new')) {
    showErr.value = true;
    noValidMessage.value = t('new_password_validation');
    return;
  }

  if (!validation('repeat')) {
    showErr.value = true;
    noValidMessage.value = t('repeat_password_validation');
    return;
  }

  if (oldPassword.value === '') {
    showErr.value = true;
    noValidMessage.value = t('old_password_empty');
    return;
  }

  isSaving.value = true;
  try {
    const res = await http.post('/users/passwords', {
      old_password: oldPassword.value,
      password: newPassword.value,
      re_password: repeatPassword.value,
    });

    const ok = res?.data?.body === true || res?.data === true;
    if (ok) {
      showErr.value = false;
      showSuccess.value = true;
      oldPassword.value = '';
      newPassword.value = '';
      repeatPassword.value = '';
    }
  } catch (err) {
    const message =
      err?.response?.data?.body?.[0]?.message ||
      err?.response?.data?.errors?.[0]?.message ||
      '';
    showErr.value = true;
    noValidMessage.value = message === 'WRONG_PASSWORD' ? t('password_error') : t('password_error');
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  topbarStore.setTitle(t('change-password'));
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => {
      router.push('/settings');
    },
  });
});
</script>

<style scoped lang="scss">
.change-password-page {
  position: relative;
}

.alert-line {
  width: 100%;
  display: flex;
  align-items: center;
  color: #fff;
  font-weight: 500;
  min-height: 56px;
  padding: 10px 16px;
  font-size: 14px;
}

.alert-line--danger {
  background: #c0392b;
}

.alert-line--success {
  background: #2ea44f;
}

.form-wrap {
  margin-top: 12px;
  padding: 0 16px;
}

.input__group {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  align-items: center !important;
  display: flex;
}

.password__input {
  box-shadow: none !important;
  border: none !important;
  height: 52px !important;
  color: rgba(0, 0, 0, 0.54);
  width: 100%;
  font-size: 15px;
  outline: none;
}

.icon__prepend {
  border: none;
  background: transparent;
  color: #828282;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.buttons__group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.button__body {
  padding: 4px 20px !important;
  border: none !important;
  height: 36px;
  background: #36b453 !important;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.24);
  border-radius: 2px;
  color: #fff;
  font-weight: 500;
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.button__body:disabled {
  opacity: 0.6;
}

@media screen and (max-width: 768px) {
  .button__body {
    width: 90% !important;
  }
}

@media screen and (min-width: 768px) {
  .button__body {
    width: 650px !important;
  }
}
</style>
